import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Hammer, ShowerHead, Sofa } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import SplitSection from "@/components/sections/SplitSection";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/painting-hero.jpg";
import supportImage from "@/assets/bg-interior-2.jpg";
import sellerImage from "@/assets/service-sell.jpg";
import notSellingImage from "@/assets/about-lifestyle.jpg";
import cleaningImage from "@/assets/cleaning-hero.jpg";
import carpentryImage from "@/assets/carpentry-hero.jpg";
import interiorDesignImage from "@/assets/interior-design-hero.jpg";
import beforeAfterImage from "@/assets/bg-interior-1.jpg";
import propertyPrepImage from "@/assets/property-prep-hero.jpg";

const paintingBenefits = [
  "Older than it is",
  "Poorly maintained",
  "Less valuable",
];

const paintingResults = [
  "Brighten and open up spaces",
  "Create a clean, cohesive look",
  "Make the home feel newer",
  "Increase emotional connection with buyers",
];

const paintingFocusAreas = [
  {
    label: "Painting Focus",
    title: "Neutral colors with broader buyer appeal",
    image: heroImage,
    imageAlt: "Freshly painted room with clean neutral tones",
  },
  {
    label: "Painting Focus",
    title: "Consistency across rooms and transitions",
    image: supportImage,
    imageAlt: "Consistent room-to-room paint presentation in a home interior",
  },
  {
    label: "Painting Focus",
    title: "Walls, trim, and visible wear addressed cleanly",
    image: propertyPrepImage,
    imageAlt: "Fresh wall and trim painting detail in a home",
  },
  {
    label: "Painting Focus",
    title: "Brightness that improves photos and showings",
    image: sellerImage,
    imageAlt: "Bright interior enhanced by strategic painting before listing",
  },
];

const beforeItems = [
  "Outdated or mismatched colors",
  "Scuffs, marks, and visible wear",
  "A home that feels heavier and older",
];

const afterItems = [
  "Clean, modern, and cohesive look",
  "Brighter, more open spaces",
  "A home that feels refreshed and move-in ready",
  "Stronger first impressions in photos and showings",
];

const sellerBenefits = [
  "Performs better in listing photos",
  "Attracts more attention",
  "Feels more valuable to buyers",
  "Supports stronger offers",
];

const relatedServices = [
  {
    title: "Cleaning services to create a polished final result",
    description:
      "Professional cleaning helps fresh paint read better and makes every room feel more complete.",
    href: "/cleaning",
    ctaLabel: "Explore Cleaning",
    image: cleaningImage,
    imageAlt: "Cleaning services that support painting preparation",
    icon: ShowerHead,
  },
  {
    title: "Carpentry and repairs to fix imperfections before painting",
    description:
      "Repairing trim, surfaces, and visible wear helps the final paint result feel intentional and finished.",
    href: "/carpentry",
    ctaLabel: "Explore Carpentry",
    image: carpentryImage,
    imageAlt: "Carpentry services paired with painting preparation",
    icon: Hammer,
  },
  {
    title: "Interior design guidance to ensure everything feels cohesive",
    description:
      "Design guidance helps color, furnishings, and presentation work together more effectively.",
    href: "/interior-design",
    ctaLabel: "Explore Interior Design",
    image: interiorDesignImage,
    imageAlt: "Interior design guidance for a cohesive painted home",
    icon: Sofa,
  },
];

const expectations = [
  "Clear guidance on what to paint and what to leave as is",
  "Strategic color recommendations based on market appeal",
  "Clean, professional execution",
  "A space that feels refreshed, modern, and complete",
];

const PaintingPage = () => {
  useEffect(() => {
    document.title = "Professional Painting Services in Massachusetts | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Transform your home with professional painting services. Hadassah Homes provides strategic painting to increase value and create immediate impact."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/painting";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Painting Services"
        title="Professional Painting Services in Massachusetts"
        subtitle="The Fastest Way to Make Your Home Feel New Again"
        backgroundImage={heroImage}
      >
        <div className="mx-auto max-w-xl space-y-5 md:space-y-6">
          <p className="text-white text-lg md:text-xl leading-[1.75]">
            Fresh, well-executed paint makes your home feel cleaner, brighter, more modern, and more move-in ready within days.
          </p>
          <Button variant="gold" size="lg" asChild>
            <Link to="/consultation">Book a Consultation</Link>
          </Button>
        </div>
      </PageHero>

      <ContentSection>
        <SplitSection
          title="Paint Changes How a Home Is Perceived"
          description="If there's one update that can instantly change how your home looks, feels, and is valued, it's paint."
          image={supportImage}
          imageAlt="Freshly painted home interior with bright, modern presentation"
        >
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
            <p>Worn walls, outdated colors, and visible marks quietly lower perceived value.</p>
            <p>Fresh, well-executed paint does the opposite. It makes your home feel cleaner, brighter, more modern, and more move-in ready within days.</p>
            <p>At Hadassah Homes, we provide professional painting services across Massachusetts designed to create immediate impact and position your home for better results.</p>
          </div>
          <div className="pt-3">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent transition-colors hover:text-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Schedule your painting consultation today
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader title="Why Paint Has Such a Big Impact" align="left" />
            <div className="space-y-4 text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              <p>Buyers react to what they see first.</p>
              <p>Color, brightness, and condition shape how a home is experienced before anything else.</p>
              <p>This is one of the simplest ways to influence perception at scale.</p>
            </div>
          </div>

          <div className="space-y-8 md:space-y-10 border-l border-accent/22 pl-6 md:pl-8">
            <div>
              <p className="mb-5 font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                Outdated or damaged paint can make a home feel
              </p>
              <div className="space-y-5 md:space-y-6">
                {paintingBenefits.map((item, index) => (
                  <div
                    key={item}
                    className={index < paintingBenefits.length - 1 ? "border-b border-secondary-foreground/10 pb-5 md:pb-6" : ""}
                  >
                    <p className="font-headline text-[1.45rem] md:text-[1.9rem] text-secondary-foreground leading-[1.25]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t border-accent/28">
              <p className="mb-4 font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                Fresh paint can
              </p>
              <div className="space-y-4">
                {paintingResults.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-accent" />
                    <p className="text-secondary-foreground text-base md:text-lg leading-[1.75]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="This Is Not Just Painting. It's Positioning"
          description="Anyone can paint a wall. The difference is choosing the right approach for the outcome you want."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8">
          {paintingFocusAreas.map((item) => (
            <article
              key={item.title}
              className="group relative min-h-[360px] overflow-hidden border border-border/60"
            >
              <img
                src={item.image}
                alt={item.imageAlt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-transparent" />
              <div className="relative z-10 flex min-h-[360px] items-end p-6 md:p-8">
                <div className="max-w-[24rem]">
                  <p className="mb-3 font-body text-[10px] uppercase tracking-[0.2em] text-white/70">
                    {item.label}
                  </p>
                  <h3 className="font-headline text-[1.55rem] md:text-[1.8rem] leading-[1.15] text-white">
                    {item.title}
                  </h3>
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="max-w-3xl mx-auto pt-10 md:pt-12 text-center text-muted-foreground text-base md:text-lg leading-[1.8]">
          The goal is not personal taste. The goal is to make your home appeal to the widest range of buyers.
        </p>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          title="Before and After: The Transformation"
          description="The difference is immediate and easy to feel."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-secondary-foreground/12">
          <article className="border-b border-secondary-foreground/12 bg-secondary/85 md:border-b-0 md:border-r md:border-r-accent/28">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Home interior before professional painting"
                className="absolute inset-0 h-full w-full object-cover brightness-[0.62] saturate-[0.7]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/42" />
              <div className="absolute left-5 top-5 z-10 border border-white/18 bg-black/58 px-3 py-1.5">
                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white">
                  Before
                </span>
              </div>
            </div>
            <div className="px-6 py-6 md:px-8 md:py-8">
              <div className="space-y-4 max-w-md">
                {beforeItems.map((item) => (
                  <p key={item} className="text-secondary-foreground/82 text-base md:text-lg leading-[1.8]">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </article>

          <article className="bg-secondary/85">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Home interior after professional painting"
                className="absolute inset-0 h-full w-full object-cover brightness-[1.05] saturate-[1.02] contrast-[1.03]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/28 via-black/12 to-transparent" />
              <div className="absolute left-5 top-5 z-10 border border-accent/35 bg-black/34 px-3 py-1.5">
                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white">
                  After
                </span>
              </div>
            </div>
            <div className="px-6 py-6 md:px-8 md:py-8">
              <div className="space-y-4 max-w-md">
                {afterItems.map((item) => (
                  <p key={item} className="text-secondary-foreground/82 text-base md:text-lg leading-[1.8]">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </article>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="For Sellers: One of the Highest Return Updates"
          description="If you are preparing to sell, painting is one of the most effective improvements you can make."
          image={sellerImage}
          imageAlt="Seller preparation with freshly painted home interior"
          reverse
        >
          <div className="space-y-4 text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
            {sellerBenefits.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-accent" />
                <p>{item}</p>
              </div>
            ))}
            <p className="pt-2">
              In many cases, a relatively small investment in painting leads to a noticeably better sale outcome.
            </p>
          </div>
          <div className="pt-3">
            <Link
              to="/sell-your-home"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent transition-colors hover:text-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              Explore Sell Your Home
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <SplitSection
          title="Not Selling? Still Worth Doing"
          description="Painting is not just for the market."
          image={notSellingImage}
          imageAlt="Well-maintained home interior with refreshed paint"
          reverse
        >
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
            <p>If you are staying in your home, updated paint refreshes your environment, improves how your home feels day to day, and keeps your space looking well maintained.</p>
            <p>It is one of the easiest ways to upgrade your home without major renovation and helps protect long-term value.</p>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Part of a Complete Preparation Strategy"
          description="Painting works best when paired with the right finishing touches."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {relatedServices.map((item) => (
            <article
              key={item.title}
              className="overflow-hidden border border-border/65 bg-card"
            >
              <div className="aspect-[16/11] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.imageAlt}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="space-y-4 px-6 py-6 md:px-7 md:py-7">
                <div className="flex items-center gap-3 text-accent">
                  <item.icon className="h-4 w-4" />
                  <span className="font-body text-[10px] uppercase tracking-[0.16em]">
                    Related Service
                  </span>
                </div>
                <h3 className="font-headline text-2xl leading-[1.2] text-foreground">
                  {item.title}
                </h3>
                <p className="text-base leading-[1.75] text-muted-foreground">
                  {item.description}
                </p>
                <Link
                  to={item.href}
                  className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent transition-colors hover:text-accent/80"
                >
                  {item.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
        <div className="mx-auto max-w-3xl pt-14 md:pt-16">
          <div className="mx-auto mb-6 h-px w-16 bg-accent/45" />
          <p className="text-center text-lg leading-[1.85] tracking-[0.01em] text-muted-foreground md:text-[1.18rem]">
            Together, these create a home that looks better, feels better, and performs better.
          </p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader title="What You Can Expect" align="left" />
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              We focus on what creates the biggest impact, not unnecessary work.
            </p>
          </div>

          <div className="space-y-7 md:space-y-8 border-l border-accent/22 pl-6 md:pl-8">
            {expectations.map((item, index) => (
              <div
                key={item}
                className={index < expectations.length - 1 ? "border-b border-secondary-foreground/10 pb-7 md:pb-8" : ""}
              >
                <p className="font-headline text-[1.35rem] md:text-[1.85rem] leading-[1.28] text-secondary-foreground">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-28 md:py-36">
        <img
          src={heroImage}
          alt="Freshly painted home prepared for sale"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/94 via-black/80 to-black/58" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent" />

        <div className="relative z-10 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-[42rem] text-center">
            <h2 className="mb-5 font-headline text-3xl leading-[1.08] text-white sm:text-4xl md:text-[3.45rem]">
              Let&apos;s Refresh Your Home the Right Way
            </h2>
            <p className="mx-auto mb-12 max-w-[36rem] text-base leading-[1.8] text-white/90 md:text-lg">
              The right paint can completely change how your home is seen, experienced, and valued. We will help you choose the right approach, execute it properly, and position your home for the best possible outcome.
            </p>
            <Button variant="gold" size="lg" asChild>
              <Link to="/consultation">Book a Consultation</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default PaintingPage;
